import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../utils/api";

const AddInvestment = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [client, setClient] = useState(null);
  const [formData, setFormData] = useState({
    amount: "",
    interestRate: "",
    durationValue: "",
    durationUnit: "months",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!userId) return; // bail if no id
    // console.log("Fetching client details for:", userId);
    const fetchClient = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/admin/clients/${userId}`);
        // console.log("API response:", res.data);
        setClient(res.data.data); // <-- use res.data.data
      } catch (err) {
        setErrorMessage("Failed to load client data");
        console.error("fetchClient error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchClient();
  }, [userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "durationUnit") {
      const oldUnit = formData.durationUnit;
      const newUnit = value;
      let newDurationValue = formData.durationValue;
      if (newDurationValue && !isNaN(parseFloat(newDurationValue))) {
        if (oldUnit === "months" && newUnit === "years") {
          newDurationValue = (parseFloat(newDurationValue) / 12).toString();
        } else if (oldUnit === "years" && newUnit === "months") {
          newDurationValue = (parseFloat(newDurationValue) * 12).toString();
        }
      }
      setFormData((prev) => ({
        ...prev,
        durationValue: newDurationValue,
        durationUnit: newUnit,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = "Amount must be greater than 0";
    }
    if (!formData.interestRate || parseFloat(formData.interestRate) <= 0) {
      newErrors.interestRate = "Interest Rate must be greater than 0";
    }
    if (!formData.durationValue || parseFloat(formData.durationValue) <= 0) {
      newErrors.durationValue = "Duration must be greater than 0";
    }
    setErrors(newErrors);
    return !Object.keys(newErrors).length;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setErrorMessage("");

      const durationInMonths =
        formData.durationUnit === "years"
          ? parseFloat(formData.durationValue) * 12
          : parseFloat(formData.durationValue);
      const investmentData = {
        client: userId,
        amount: parseFloat(formData.amount),
        interestRate: parseFloat(formData.interestRate),
        duration: parseInt(durationInMonths),
      };

      await api.post(`/admin/clients/${userId}/investments`, investmentData);

      setSuccessMessage("Investment added successfully!");

      // Redirect to client investments page after a short delay
      setTimeout(() => {
        navigate(`/admin/clients/${userId}/investments`);
      }, 1000);
    } catch (err) {
      setErrorMessage(
        err.response?.data?.message || "Failed to add investment"
      );
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-center py-4">Loading client data...</div>;
  }

  return (
    <div className="card main-content max-w-xl">
      <div className="card-header">
        <h3>Add New Investment for {client?.name}</h3>
      </div>
      <div className="card-body">
        {successMessage && (
          <div className="alert alert-success">{successMessage}</div>
        )}

        {errorMessage && (
          <div className="alert alert-danger">{errorMessage}</div>
        )}

        <div className="flex flex-col gap-1 mb-3">
          <span>
            <strong>User ID:</strong> {client?.userId}
          </span>
          <span>
            <strong>Email:</strong> {client?.email}
          </span>
          <span>
            <strong>Phone:</strong> {client?.phone}
          </span>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="input-row">
            <div className="form-group">
              <label>Amount (₹)</label>
              <input
                name="amount"
                type="number"
                value={formData.amount}
                onChange={handleChange}
                className="form-control"
                required
                step="0.01"
              />
              {errors.amount && <p className="text-red-600">{errors.amount}</p>}
            </div>

            <div className="form-group">
              <label>Interest Rate (%)</label>
              <input
                name="interestRate"
                type="number"
                value={formData.interestRate}
                onChange={handleChange}
                className="form-control"
                required
                step="0.01"
              />
              {errors.interestRate && (
                <p className="text-red-600">{errors.interestRate}</p>
              )}
            </div>

            <div className="form-group">
              <label>Duration</label>
              <div className="flex gap-2">
                <input
                  name="durationValue"
                  type="number"
                  value={formData.durationValue}
                  onChange={handleChange}
                  className="form-control flex-grow"
                  required
                />
                <select
                  name="durationUnit"
                  value={formData.durationUnit}
                  onChange={handleChange}
                  className="form-control w-32"
                >
                  <option value="months">Months</option>
                  <option value="years">Years</option>
                </select>
              </div>
              {errors.durationValue && (
                <p className="text-red-600">{errors.durationValue}</p>
              )}
            </div>
          </div>

          <div className="flex mt-3 gap-2">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate(`/admin/clients/${userId}/investments`)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? "Adding..." : "Add Investment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddInvestment;
